import { useMemo } from "react";

// Map a free-text sky condition to one of our FX buckets.
export function fxCategory(condition = "") {
  const c = (condition || "").toLowerCase();
  if (c.includes("thunder") || c.includes("tstorm") || c.includes("storm")) return "storm";
  if (c.includes("snow") || c.includes("sleet") || c.includes("flurr") || c.includes("wintry") || c.includes("ice")) return "snow";
  if (c.includes("rain") || c.includes("shower") || c.includes("drizzle")) return "rain";
  if (c.includes("fog") || c.includes("mist") || c.includes("haze") || c.includes("smoke")) return "fog";
  if (c.includes("sun") || c.includes("clear") || c.includes("fair")) return "sun";
  if (c.includes("cloud") || c.includes("overcast")) return "cloud";
  return "cloud";
}

// The statewide "mood" = the most common bucket across the reporting cities.
export function dominantFx(cities = []) {
  const counts = {};
  for (const c of cities) {
    const k = fxCategory(c?.condition || "");
    counts[k] = (counts[k] || 0) + 1;
  }
  let best = "cloud", n = -1;
  for (const [k, v] of Object.entries(counts)) if (v > n) { best = k; n = v; }
  return best;
}

// Subtle, condition-driven motion drawn over a full-bleed map slide.
// Pure CSS animations (see index.css .mapfx-*), no backend, pointer-events: none.
export default function MapWeatherFx({ cities, category }) {
  const cat = category || dominantFx(cities || []);

  const drops = useMemo(
    () => Array.from({ length: 32 }, () => ({
      left: Math.random() * 100,
      delay: -Math.random() * 1.4,
      dur: 0.75 + Math.random() * 0.55,
      len: 44 + Math.random() * 46,
      op: 0.35 + Math.random() * 0.4,
    })),
    []
  );
  const flakes = useMemo(
    () => Array.from({ length: 24 }, () => ({
      left: Math.random() * 100,
      delay: -Math.random() * 6,
      dur: 6 + Math.random() * 5,
      size: 4 + Math.random() * 7,
      op: 0.5 + Math.random() * 0.4,
    })),
    []
  );
  const clouds = useMemo(
    () => Array.from({ length: 5 }, (_, i) => ({
      top: 6 + i * 16 + Math.random() * 6,
      w: 360 + Math.random() * 320,
      h: 120 + Math.random() * 90,
      delay: -Math.random() * 60,
      dur: 70 + Math.random() * 45,
      op: 0.35 + Math.random() * 0.3,
    })),
    []
  );

  if (cat === "sun") {
    return (
      <div className="mapfx" data-testid="mapfx-sun" data-fx="sun">
        <div className="mapfx-sun" />
        <div className="mapfx-sun-core" />
      </div>
    );
  }
  if (cat === "fog") {
    return (
      <div className="mapfx" data-testid="mapfx-fog" data-fx="fog">
        <div className="mapfx-fog" />
      </div>
    );
  }
  if (cat === "snow") {
    return (
      <div className="mapfx" data-testid="mapfx-snow" data-fx="snow">
        {flakes.map((f, i) => (
          <span key={i} className="mapfx-flake" style={{ left: `${f.left}%`, width: f.size, height: f.size, opacity: f.op, animationDelay: `${f.delay}s`, animationDuration: `${f.dur}s` }} />
        ))}
      </div>
    );
  }
  if (cat === "rain" || cat === "storm") {
    return (
      <div className="mapfx" data-testid={`mapfx-${cat}`} data-fx={cat}>
        {cat === "storm" && <div className="mapfx-flash" />}
        {drops.map((d, i) => (
          <span key={i} className="mapfx-drop" style={{ left: `${d.left}%`, height: d.len, opacity: d.op, animationDelay: `${d.delay}s`, animationDuration: `${d.dur}s` }} />
        ))}
      </div>
    );
  }
  // cloud (default)
  return (
    <div className="mapfx" data-testid="mapfx-cloud" data-fx="cloud">
      {clouds.map((c, i) => (
        <div key={i} className="mapfx-cloud" style={{ top: `${c.top}%`, width: c.w, height: c.h, opacity: c.op, animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s` }} />
      ))}
    </div>
  );
}
